import { captureExceptionWithSentry } from '../../../../sentry'
import * as React from 'react'
import { Col, FormControl, OverlayTrigger, Row, Tab, Tabs, Tooltip } from 'react-bootstrap'
import IconButton from '../../../../../src/common/IconButton'
import Location from '../../adLocations/Location'
import { Article, SaveResponse, Variation } from '../articlesContainer'
import ArticlePanel from './ArticlePanel'
import ArticleSettings from './ArticleSettings'
import ArticleStats from './ArticleStats'
import EditInstantArticleButton from './InstantArticles/EditInstantArticleButton'
import ManageAiVariationsButton from './manageAiVariationsButton/ManageAiVariationsButton'
import EditImageButton from './editImageButton/EditImageButton'
import { AdFormats } from '../../adFormat/adFormat'
import { CustomEditorModal } from '../../../../aiImageEditor/editor/CustomEditorModal'

interface ArticleTabsProps {
	articles: Article[]
	updateVariationField: (
		artUid: string,
		variationNum: number,
		field: keyof Variation,
		content: string,
		instant?: boolean,
	) => void
	duplicateVariation: (articleId: string, variationNo: number, instant?: boolean) => void
	removeVariation: (articleId: string, variationNo: number, instant?: boolean) => void
	addVariation: (articleId: string, instant?: boolean) => void
	updateArticle: (art: Article, instant?: boolean) => void
	page?: 'ccp' | 'campaign'
	selectedLocations?: Location[]
	campaignIsActive?: boolean
	showInstantArticleModal?: (article: string) => void
	activeTab?: number
	instant?: boolean
	showVariations?: boolean
	client?: {
		id: number
		org: string
	}
	campaignId?: number
	hasUnsavedChanges?: boolean
	adFormat: AdFormats
}

interface objectType {
	article_title: string
}

const ArticleTabs: React.FunctionComponent<ArticleTabsProps> = (props) => {
	const [activeKey, setActiveKey] = React.useState(0)
	const [editing, setEditing] = React.useState(false)
	const [title, setTitle] = React.useState('')
	const [allowSubmit, setAllowSubmit] = React.useState(false)
	const [articleNumber, setArticleNumber] = React.useState(0)
	const [alertMessages, setAlertMessages] = React.useState('')
	const [alertVariant, setAlertVariant] = React.useState('')
	const [isImageEditorOpen, setIsImageEditorOpen] = React.useState(false)

	const titleWithOverlay = (article: Article, index: number) => {
		return (
			<div>
				<OverlayTrigger
					trigger={['hover']}
					placement={'top'}
					overlay={
						<Tooltip id={article.uid}>{article.variations[0].title || `Article ${index + 1}`}</Tooltip>
					}
				>
					<span>{`Article ${index + 1}`}</span>
				</OverlayTrigger>
			</div>
		)
	}

	const changeStateValueOnEditMode = (ArtTitle: string, index: number) => {
		setEditing(true)
		setTitle(ArtTitle)
		setArticleNumber(index)
	}

	const changeTitle = (e: any) => {
		const value = e.target.value
		setTitle(value)
		if (value.length > 0 && value.length <= 70) {
			setAllowSubmit(true)
		} else {
			setAllowSubmit(false)
		}
	}

	React.useEffect(() => {
		if (props.activeTab) {
			setActiveKey(props.activeTab)
		}
	}, [props.activeTab])

	const handleTabSelect = (key: any) => {
		setActiveKey(key)
	}

	const displayAlertMessage = (message: string, alertType: string) => {
		setAlertMessages(message)
		setAlertVariant(alertType)
		setAllowSubmit(false)
		setTimeout(() => {
			setAlertMessages('')
			setAlertVariant('')
		}, 5000)
	}

	const handleEditImage = () => {
		// Simply open the image editor modal
		setIsImageEditorOpen(true)
	}

	const updateTitle = async (articleTitle: string, article: Article) => {
		const articleId = article.articleId
		if (!articleId) {
			displayAlertMessage('Article has to be saved before adding the title', 'alert alert-danger')
			return
		}
		const data = {
			article_title: articleTitle,
		}
		const result = await saveData(data, `/api/ad_campaigns/${props.campaignId}/articles/${articleId}`, {
			method: 'PATCH',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/json',
			},
		})
		if (result.status === 'error') {
			displayAlertMessage('There was an error while update ad group title', 'alert alert-danger')
			return
		}
		props.updateArticle({ ...article, article_title: title }, false)
		setArticleNumber(0)
		setEditing(false)
		displayAlertMessage('Ad group title is saved successfully', 'alert alert-success')
		return result
	}

	const saveData = async (data: objectType, url: string, headers: RequestInit): Promise<SaveResponse> => {
		if (data) {
			headers.body = JSON.stringify(data)
		}
		try {
			const response = await fetch(url, headers)
			if ((response.ok && response.status === 200) || response.status === 201) {
				const result = (await response.json()) as SaveResponse
				return result
			} else {
				return {
					status: 'OK',
				}
			}
		} catch (error) {
			captureExceptionWithSentry(error)
			return {
				status: 'error',
				message: error as string,
			}
		}
	}

	return (
		<>
			<Tabs bsStyle="pills" activeKey={activeKey} onSelect={handleTabSelect} id="articles">
				{props.articles.map((art, index) => (
					<Tab
						key={art.uid}
						eventKey={index}
						title={
							art.variations.length > 0 ? (
								titleWithOverlay(art, index)
							) : (
								<i className="fa fa-spinner fa-spin" />
							)
						}
					>
						{props.showInstantArticleModal && (
							<Row>
								{props.page === 'campaign' && (
									<Col
										xs={12}
										sm={7}
										md={5}
										className="text-info"
										style={{ fontSize: '22px', marginTop: '20px', fontWeight: 'bold' }}
									>
										{art.instantArticle?.headline}
									</Col>
								)}
								<Col sm={4} md={3}>
									<EditInstantArticleButton
										showInstantArticle={() => {
											props.showInstantArticleModal!(art.uid)
										}}
										articleNum={index + 1}
									/>
								</Col>
							</Row>
						)}
						{editing && index === articleNumber ? (
							<Row style={{ marginTop: '2rem' }} className="text-info form-inline">
								<Col md={12} sm={10}>
									<strong style={{ fontSize: '2rem' }}>Ad group {index + 1}:&nbsp;&nbsp;</strong>
									<OverlayTrigger
										trigger={['focus']}
										placement="bottom"
										overlay={
											<Tooltip>
												{(title && title.length) || 0} / 70 characters used <br /> (Minimum 1
												character)
											</Tooltip>
										}
									>
										<FormControl
											type="text"
											value={title}
											onChange={changeTitle}
											style={{ width: '60rem', marginRight: '2rem' }}
											maxLength={70}
											required
										/>
									</OverlayTrigger>
									<IconButton
										tooltipText="Cancel Editing"
										onClick={() => setEditing(false)}
										icon="fa-times text-danger"
									/>
									&nbsp;&nbsp;&nbsp;
									{allowSubmit && (
										<IconButton
											tooltipText="Save"
											onClick={() => updateTitle(title, art)}
											icon="fa-check text-success"
										/>
									)}
								</Col>
							</Row>
						) : (
							<>
								<Row style={{ marginTop: '2rem' }} className="text-info">
									<Col md={12} sm={10}>
										<strong style={{ fontSize: '2rem', marginRight: '2rem' }}>
											Ad group {index + 1}: {art.article_title}
										</strong>

										<IconButton
											tooltipText="Edit"
											onClick={() =>
												changeStateValueOnEditMode(art.article_title as string, index)
											}
											icon="fa-edit text-success"
											data-test="edit-client-button"
										/>
									</Col>
								</Row>
								{props.page === 'campaign' && art.articleId && (
									<Row style={{ marginTop: '2rem' }} className="text-info">
										<Col md={12} sm={10}>
											<ManageAiVariationsButton
												campaignId={props.campaignId}
												adGroupNumber={index + 1}
												adGroupId={art.articleId}
												advertiserName={art.client_name}
												refreshParentArticle={() => props.updateArticle(art, true)}
												askToSaveFirst={props.hasUnsavedChanges}
												adFormat={props.adFormat}
											/>
											{window.location.search.includes('ai-image-editor') &&
												props.adFormat !== AdFormats.Video && (
													<EditImageButton
														onClick={() => handleEditImage()}
														disabled={props.hasUnsavedChanges}
													/>
												)}
										</Col>
									</Row>
								)}
							</>
						)}

						{alertMessages && (
							<Row>
								<Col md={6} className={alertVariant} role="alert">
									{alertMessages}
								</Col>
							</Row>
						)}

						{props.page === 'campaign' && <ArticleStats article={art} />}

						{(props.showVariations === undefined || props.showVariations) && (
							<>
								<ArticleSettings
									index={index}
									active={true}
									article={art}
									updateArticle={props.updateArticle}
									page={props.page}
								/>
								<ArticlePanel
									article={art}
									updateVariationField={props.updateVariationField}
									duplicateVariation={props.duplicateVariation}
									removeVariation={props.removeVariation}
									addVariation={props.addVariation}
									selectedLocations={props.selectedLocations}
									campaignIsActive={props.campaignIsActive}
									instant={props.instant}
									client={props.client}
									campaignId={props.campaignId}
									adFormat={props.adFormat}
								/>
							</>
						)}
					</Tab>
				))}
			</Tabs>

			{/* Image Editor Modal */}
			<CustomEditorModal
				isOpen={isImageEditorOpen}
				onClose={() => setIsImageEditorOpen(false)}
				onAccept={() => {
					// The modal will handle saving internally
					// We don't need to do anything here for now
				}}
			/>
		</>
	)
}

export default ArticleTabs
